import { useState, useEffect, useCallback, type CSSProperties } from 'react'
import { ConfigVersionHistoryButton } from '../components/ConfigVersionHistory'
import { targetsApi, type Target } from '../api/performance'
import { agentsApi, type AgentProfile } from '../api/team'
import {
  FormSection, Page, PageHeader, Callout, DataTable, DataTableEmptyRow, Pagination, LoadingState, LinearProgress,
  StatusPill, Dialog, Button, IconButton, TextField, Select, Icon, useSnackbar, type PillTone, FilterBar,
  useConfirm,
} from '../components/ui'

const TARGET_TYPE_LABELS: Record<string, string> = {
  revenue: 'Fatturato', contracts: 'Contratti', leads: 'Lead',
  appointments: 'Appuntamenti', calls: 'Chiamate',
  conversion_rate: 'Tasso conversione', custom: 'Personalizzato',
}

const SCOPE_TYPE_LABELS: Record<string, string> = {
  company: 'Azienda', department: 'Reparto', team: 'Team', agent: 'Agente',
}

interface TargetForm {
  name: string; description: string; targetType: string
  scopeType: string; targetValue: string; startDate: string; endDate: string
  /** Holds `AgentProfile.userId` (as a string, for the controlled <select>) when
   * scopeType === 'agent' — NOT `AgentProfile.id`. See TargetsController.cs's XML doc on
   * `Target.ScopeId`: for scope "agent" it must live in the same id-space as
   * `identity.users.Id`, which is what `AgentProfile.userId` is; `AgentProfile.id` is the
   * profile row's own PK, a different sequence. Sending `.id` here would silently create a
   * Target whose live progress always computes to 0 (TASK-0040's one critical detail). */
  scopeAgentUserId: string
}

const EMPTY_FORM: TargetForm = {
  name: '', description: '', targetType: 'revenue',
  scopeType: 'company', targetValue: '0', startDate: '', endDate: '', scopeAgentUserId: '',
}

/** Same level of agent identification `TeamPage.tsx` already uses (ID column + "User ID"
 * column + `code`) — `AgentProfile` has no name field anywhere in this program (verified:
 * id/userId/agencyId/departmentId/code/isActive/createdAt only), and TeamPage, the only
 * other page listing agents, doesn't resolve one either. Not inventing a new mechanism here. */
function agentLabel(a: AgentProfile): string {
  return a.code ? `${a.code} — agente #${a.userId}` : `Agente #${a.userId}`
}

function statusBadge(s: string) {
  const map: Record<string, PillTone> = {
    active: 'success', completed: 'info', cancelled: 'neutral',
  }
  const labels: Record<string, string> = { active: 'Attivo', completed: 'Completato', cancelled: 'Annullato' }
  return <StatusPill tone={map[s] ?? 'neutral'}>{labels[s] ?? s}</StatusPill>
}

function formatDate(s: string): string {
  return new Date(s).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** TASK-0089: `TargetsController.List`/`Get` now OMIT `currentValue` and `percentage` when the
 * caller has no `CanSeeRealAmounts` (and is not Admin). A target's live progress is computed from
 * real contract amounts read company-wide (`IContractContributionReader`) — for an `agent`-scope
 * target that is one named colleague's real revenue, exactly what ADR-008 keeps away from a
 * non-privileged agent on `/api/contracts`. Both keys are absent, not null/0, so
 * `percentage.toFixed` / `currentValue.toLocaleString` would throw on `undefined` and a bar
 * rendered from a missing value would show `NaN%`.
 *
 * Degrades the same way `PaymentsPage.tsx`/`ReportsPage.tsx`/`CommissionsPage.tsx`'s `AmountCell`
 * (TASK-0090) does for the equivalent Finance redaction: a muted lock glyph plus a tooltip, never
 * a fabricated zero — a `0%` bar would read as "this target has made no progress", which is a
 * different (and false) statement from "you may not see this figure".
 *
 * Props are typed `number | undefined` although `api/performance.ts` still declares
 * `currentValue: number` / `percentage: number` as required — that file is outside this task's
 * allowed_paths. Passing the (declared-required) value into an optional parameter is what lets the
 * runtime guard type-check, since an inline `t.percentage === undefined` on a `number` is rejected
 * as an impossible comparison (TS2367). Flagged to the Lead: the API types should become
 * `currentValue?: number` / `percentage?: number`, as TASK-0090 already did in
 * `api/finance-ext.ts`. */
function RedactedProgress() {
  return (
    <span className="text-muted d-inline-flex align-items-center gap-1" title="Progresso non visibile">
      <Icon name="lock" size={18} />
      <span className="md-typescale-body-small">Non visibile</span>
    </span>
  )
}

function ProgressBar({ percentage }: { percentage: number | undefined }) {
  if (percentage === undefined) return <RedactedProgress />
  const pct = Math.min(100, Math.max(0, percentage))
  // Il colore dipende dal valore: si ridefinisce il solo ruolo primario della barra.
  const tone = pct >= 100 ? 'success' : pct >= 60 ? null : pct >= 30 ? 'warning' : 'error'
  const style = tone ? ({ '--md-primary': `var(--md-${tone})` } as CSSProperties) : undefined
  return (
    <div className="d-flex align-items-center gap-2">
      <LinearProgress className="flex-grow-1" value={pct} style={style} aria-label="Progresso obiettivo" />
      <span className="text-muted md-typescale-body-small md-num">{pct.toFixed(0)}%</span>
    </div>
  )
}

function ProgressValue({ current, target }: { current: number | undefined; target: number }) {
  if (current === undefined) return <>— / {target.toLocaleString('it-IT')}</>
  return <>{current.toLocaleString('it-IT')} / {target.toLocaleString('it-IT')}</>
}

export function TargetsPage() {
  const snackbar = useSnackbar()
  const confirm = useConfirm()
  const [items, setItems] = useState<Target[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [form, setForm] = useState<TargetForm>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const [agents, setAgents] = useState<AgentProfile[]>([])

  const pageSize = 25

  // Load every active agent for the agent-scope picker. `agentsApi.list` hard-codes
  // size=25 per call (api/team.ts), so page through it here rather than showing only the
  // first 25 agents — capped at 40 pages (1000 agents) as a sane runaway guard.
  useEffect(() => {
    let cancelled = false
    async function loadAllAgents() {
      const all: AgentProfile[] = []
      let page = 1
      for (; page <= 40; page++) {
        const res = await agentsApi.list({ isActive: true, page })
        all.push(...res.items)
        if (all.length >= res.total || res.items.length === 0) break
      }
      if (!cancelled) setAgents(all)
    }
    loadAllAgents().catch(() => { /* non-critical — picker just renders empty */ })
    return () => { cancelled = true }
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const p: { page: number; status?: string } = { page }
      if (statusFilter) p.status = statusFilter
      const res = await targetsApi.list(p)
      setItems(res.items)
      setTotal(res.total)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, page])

  useEffect(() => { void load() }, [load])

  function openCreate() {
    setForm(EMPTY_FORM); setEditId(null); setSaveError(''); setShowModal(true)
  }

  function openEdit(t: Target) {
    setForm({
      name: t.name, description: t.description ?? '',
      targetType: t.targetType, scopeType: t.scopeType,
      targetValue: String(t.targetValue),
      startDate: t.startDate.slice(0, 10), endDate: t.endDate.slice(0, 10),
      // t.scopeId already IS the identity.users.Id-space value (whatever was originally
      // submitted as ScopeId) — same value goes straight back into the picker's <select>,
      // whose <option value> is also userId (see the picker below). No id/userId
      // translation needed on read, only on write (handleSave, below).
      scopeAgentUserId: t.scopeType === 'agent' && t.scopeId != null ? String(t.scopeId) : '',
    })
    setEditId(t.id); setSaveError(''); setShowModal(true)
  }

  function closeModal() { setShowModal(false); setEditId(null); setSaveError('') }

  async function handleSave() {
    if (!form.name.trim() || !form.startDate || !form.endDate) {
      setSaveError('Nome, date inizio e fine sono obbligatori')
      return
    }
    setSaving(true); setSaveError('')
    try {
      const payload = {
        name: form.name, targetType: form.targetType, scopeType: form.scopeType,
        targetValue: parseFloat(form.targetValue) || 0,
        startDate: new Date(form.startDate).toISOString(),
        endDate: new Date(form.endDate).toISOString(),
        ...(form.description ? { description: form.description } : {}),
        // Critical: form.scopeAgentUserId is populated from AgentProfile.userId (see the
        // picker's <option value={a.userId}>, not <option value={a.id}>) — this is already
        // the correct identity.users.Id-space value, sent as-is. For company/department/team
        // scope, scopeId stays omitted -> null server-side, unchanged from before this task.
        ...(form.scopeType === 'agent' && form.scopeAgentUserId
          ? { scopeId: Number(form.scopeAgentUserId) }
          : {}),
      }
      if (editId !== null) {
        await targetsApi.update(editId, payload)
      } else {
        await targetsApi.create(payload)
      }
      closeModal(); void load()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Errore salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(t: Target) {
    if (!(await confirm({ title: 'Archiviare l\'obiettivo?', message: `Archiviare l'obiettivo "${t.name}"? Se ha già un percorso viene annullato e resta in archivio; se è ancora a zero viene rimosso.`, confirmLabel: 'Archivia', destructive: true }))) return
    try {
      const res = await targetsApi.delete(t.id)
      if (res?.archived) snackbar.show({ message: 'Obiettivo archiviato (annullato): lo storico dei progressi resta consultabile.' })
      void load()
    }
    catch (e) { snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' }) }
  }

  async function handleComplete(t: Target) {
    try { await targetsApi.updateStatus(t.id, 'completed'); void load() }
    catch (e) { snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' }) }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <Page>
      <PageHeader
        title="Obiettivi"
        icon="track_changes"
        count={total > 0 ? total : undefined}
        actions={
          <Button variant="filled" icon="add" onClick={openCreate}>
            Nuovo obiettivo
          </Button>
        }
      />

      <FilterBar>
        <Select label="Stato" value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1) }}>
          <option value="">Tutti gli stati</option>
          <option value="active">Attivi</option>
          <option value="completed">Completati</option>
          <option value="cancelled">Annullati</option>
        </Select>
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}

      {loading ? (
        <LoadingState />
      ) : (
        <DataTable>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Tipo</th>
              <th>Scope</th>
              <th className="md-num">Target</th>
              <th>Progresso</th>
              <th>Periodo</th>
              <th>Stato</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <DataTableEmptyRow colSpan={8} icon="target" title="Nessun obiettivo trovato" />}
            {items.map(t => (
              <tr key={t.id}>
                <td className="md-typescale-emphasized-body-medium">{t.name}</td>
                <td className="text-muted md-typescale-body-small">{TARGET_TYPE_LABELS[t.targetType] ?? t.targetType}</td>
                <td className="text-muted md-typescale-body-small">{SCOPE_TYPE_LABELS[t.scopeType] ?? t.scopeType}</td>
                <td className="md-typescale-emphasized-body-medium md-num">{t.targetValue.toLocaleString('it-IT')}</td>
                <td>
                  <ProgressBar percentage={t.percentage} />
                  <div className="text-muted md-typescale-body-small">
                    <ProgressValue current={t.currentValue} target={t.targetValue} />
                  </div>
                </td>
                <td className="text-muted md-typescale-body-small">
                  {formatDate(t.startDate)} →<br />{formatDate(t.endDate)}
                </td>
                <td>{statusBadge(t.status)}</td>
                <td>
                  <div className="d-flex gap-1">
                    {t.status === 'active' && (
                      <IconButton icon="check_circle" size="sm" aria-label="Segna completato"
                        onClick={() => void handleComplete(t)} />
                    )}
                    <ConfigVersionHistoryButton entityType="Target" entityId={t.id} label={`obiettivo #${t.id}`} />
                    <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEdit(t)} />
                    <IconButton icon="delete" size="sm" aria-label="Archivia" onClick={() => void handleDelete(t)} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center">
          <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} />
        </div>
      )}

      <Dialog
        open={showModal}
        onClose={closeModal}
        title={editId !== null ? 'Modifica obiettivo' : 'Nuovo obiettivo'}
        as="form"
        onSubmit={e => { e.preventDefault(); void handleSave() }}
        actions={
          <>
            <Button variant="text" onClick={closeModal}>Annulla</Button>
            <Button type="submit" variant="text" disabled={saving}>
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        {saveError && <Callout tone="error">{saveError}</Callout>}
        <FormSection title="Obiettivo">
          <TextField className="md-span-full" type="text" label="Nome *" value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <Select label="Tipo metrca" value={form.targetType}
            onChange={e => setForm(f => ({ ...f, targetType: e.target.value }))}>
            {Object.entries(TARGET_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
          <Select label="Ambito" value={form.scopeType}
            onChange={e => setForm(f => ({
              ...f,
              scopeType: e.target.value,
              // Reset the agent selection when leaving "agent" scope, so a stale
              // selection can't linger in state and get resurrected by flipping
              // scopeType back — ScopeId stays null for company/department/team.
              scopeAgentUserId: e.target.value === 'agent' ? f.scopeAgentUserId : '',
            }))}>
            {Object.entries(SCOPE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
          {form.scopeType === 'agent' && (
            <Select className="md-span-full" label="Agente" value={form.scopeAgentUserId}
              onChange={e => setForm(f => ({ ...f, scopeAgentUserId: e.target.value }))}
              supportingText="Obiettivo individuale per l'agente selezionato. Nessun nome disponibile (stesso livello di identificazione di Team & Agenti): codice agente e user ID.">
              <option value="">Seleziona agente</option>
              {agents.map(a => (
                <option key={a.id} value={a.userId}>{agentLabel(a)}</option>
              ))}
            </Select>
          )}
          <TextField type="number" step="0.01" label="Valore target" value={form.targetValue}
            onChange={e => setForm(f => ({ ...f, targetValue: e.target.value }))} />
          <TextField className="md-span-full" multiline rows={2} label="Descrizione" value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        </FormSection>
        <FormSection title="Periodo">
          <TextField type="date" label="Data inizio *" value={form.startDate}
            onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
          <TextField type="date" label="Data fine *" value={form.endDate}
            onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
        </FormSection>
      </Dialog>
    </Page>
  )
}
